import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Film, Image as ImageIcon, Loader2, RotateCcw, Search, ShieldCheck, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { OperationsRoleGate } from "@/components/OperationsRoleGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  requestEvidenceUpload, finalizeEvidenceUpload, getEvidenceUrl, deleteEvidence,
  createSighting, decideMatch, createCase, linkToCase,
} from "@/lib/evidence.functions";
import { validateEvidenceFile } from "@/lib/evidence/providers";

export const EVIDENCE_ROLES = ["org_owner", "admin", "supervisor", "site_supervisor", "dispatcher", "auditor"];

export const Route = createFileRoute("/_authenticated/operations/evidence")({
  component: () => (
    <OperationsRoleGate allowed={EVIDENCE_ROLES}>
      <EvidencePage />
    </OperationsRoleGate>
  ),
  head: () => ({ meta: [{ title: "AI Evidence — MissionReady" }, { name: "robots", content: "noindex, nofollow" }] }),
});

const STATUS_LABEL: Record<string, string> = {
  pending_upload: "Uploading", uploaded: "Uploaded", queued: "Queued", processing: "Processing",
  done: "Analyzed", failed: "Failed", not_configured: "AI not configured",
};
const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";

async function sha256(file: File) {
  const buf = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function EvidencePage() {
  const { currentOrg, currentRole } = useAuth();
  const orgId = currentOrg?.id;
  const canReview = currentRole !== "auditor";
  const [items, setItems] = useState<any[]>([]);
  const [sightings, setSightings] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [incidents, setIncidents] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoadError(null);
    const [ev, si, ma, ca, li, inc, st] = await Promise.all([
      supabase.from("evidence_items").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(300),
      supabase.from("sightings").select("*").eq("organization_id", orgId).order("event_at", { ascending: false }).limit(500),
      supabase.from("sighting_matches").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(300),
      supabase.from("evidence_cases").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
      supabase.from("evidence_case_links").select("*").eq("organization_id", orgId),
      supabase.from("incidents").select("id, reference_number, title, site_id, occurrence_at").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(200),
      supabase.from("sites").select("id, name").eq("organization_id", orgId).order("name"),
    ]);
    const err = ev.error || si.error || ma.error;
    if (err) setLoadError("Could not load evidence. Try again.");
    setItems(ev.data ?? []); setSightings(si.data ?? []); setMatches(ma.data ?? []);
    setCases(ca.data ?? []); setLinks(li.data ?? []); setIncidents(inc.data ?? []); setSites(st.data ?? []);
    setLoading(false);
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const incById = useMemo(() => Object.fromEntries(incidents.map((i) => [i.id, i])), [incidents]);
  const siteById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const ctx = { orgId: orgId!, items, sightings, matches, cases, links, incidents, sites, incById, siteById, canReview, reload: load };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-1 text-sm"><Link to="/operations" className="text-muted-foreground hover:underline">Operations Center</Link></div>
      <h1 className="font-display text-2xl font-semibold">AI Evidence</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Attach photos, video and documents to existing reports, log sightings and review possible links between reports.
        Matches are suggestions only — a reviewer must confirm them. No face recognition or identification of individuals.
      </p>
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span><strong>AI analysis not configured.</strong> Uploads, manual tagging, sightings, matching rules and cases all work without it.</span>
      </div>
      {loadError && <p role="alert" className="mt-3 text-sm text-destructive">{loadError}</p>}
      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <Tabs defaultValue="queue" className="mt-5">
          <TabsList className="flex h-auto flex-wrap">
            <TabsTrigger value="queue">Evidence</TabsTrigger>
            <TabsTrigger value="sightings">Sightings</TabsTrigger>
            <TabsTrigger value="matches">Match review ({matches.filter((m) => m.status === "pending").length})</TabsTrigger>
            <TabsTrigger value="cases">Cases</TabsTrigger>
          </TabsList>
          <TabsContent value="queue"><EvidenceQueue {...ctx} /></TabsContent>
          <TabsContent value="sightings"><SightingsPanel {...ctx} /></TabsContent>
          <TabsContent value="matches"><MatchQueue {...ctx} /></TabsContent>
          <TabsContent value="cases"><CasesPanel {...ctx} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

type Ctx = any;

function EvidenceQueue(c: Ctx) {
  const reqUp = useServerFn(requestEvidenceUpload);
  const fin = useServerFn(finalizeEvidenceUpload);
  const getUrl = useServerFn(getEvidenceUrl);
  const del = useServerFn(deleteEvidence);
  const [incidentId, setIncidentId] = useState("");
  const [progress, setProgress] = useState<Record<string, string>>({});
  const [status, setStatus] = useState(""); const [site, setSite] = useState(""); const [q, setQ] = useState("");
  const [preview, setPreview] = useState<{ item: any; url: string } | null>(null);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    if (!incidentId) { toast.error("Choose the incident report this evidence belongs to"); return; }
    for (const f of Array.from(files)) {
      const v = validateEvidenceFile(f.type, f.size);
      if (!v.ok) { toast.error(`${f.name}: ${v.error}`); continue; }
      try {
        setProgress((p) => ({ ...p, [f.name]: "Preparing…" }));
        const hash = await sha256(f);
        const r = await reqUp({ data: { incidentId, fileName: f.name, mimeType: f.type, size: f.size, sha256: hash } });
        if (r.duplicate) { toast.info(`${f.name} is already stored as evidence`); continue; }
        setProgress((p) => ({ ...p, [f.name]: "Uploading…" }));
        const { error } = await supabase.storage.from("evidence").uploadToSignedUrl(r.path!, r.token!, f, { contentType: f.type });
        if (error) throw new Error("Upload failed — retry from the list");
        setProgress((p) => ({ ...p, [f.name]: "Verifying…" }));
        await fin({ data: { evidenceId: r.evidenceId } });
        toast.success(`${f.name} uploaded`);
      } catch (e: any) {
        toast.error(`${f.name}: ${e?.message ?? "Upload failed"}`);
      } finally {
        setProgress((p) => { const n = { ...p }; delete n[f.name]; return n; });
      }
    }
    void c.reload();
  };

  const retry = async (id: string) => {
    try { await fin({ data: { evidenceId: id } }); toast.success("Verified"); } catch (e: any) { toast.error(e?.message ?? "Retry failed"); }
    void c.reload();
  };
  const open = async (item: any) => {
    try { const { url } = await getUrl({ data: { evidenceId: item.id } }); setPreview({ item, url }); }
    catch (e: any) { toast.error(e?.message ?? "Could not open"); }
  };
  const download = async (item: any) => {
    try { const { url } = await getUrl({ data: { evidenceId: item.id, download: true } }); window.location.assign(url); }
    catch (e: any) { toast.error(e?.message ?? "Could not export"); }
  };
  const remove = async (item: any) => {
    if (!confirm(`Delete ${item.original_filename}? This is permanent and logged.`)) return;
    try { await del({ data: { evidenceId: item.id } }); toast.success("Deleted"); void c.reload(); }
    catch (e: any) { toast.error(e?.message ?? "Delete failed"); }
  };

  const list = c.items.filter((i: any) =>
    (!status || i.status === status) && (!site || i.site_id === site) &&
    (!q || `${i.original_filename} ${i.description ?? ""} ${c.incById[i.incident_id]?.reference_number ?? ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4">
      {c.canReview && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-semibold">Add evidence</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="ev-inc">Incident report</label>
            <select id="ev-inc" className={sel} value={incidentId} onChange={(e) => setIncidentId(e.target.value)}>
              <option value="">Choose incident report…</option>
              {c.incidents.map((i: any) => <option key={i.id} value={i.id}>{i.reference_number} {i.title ?? ""}</option>)}
            </select>
            <Button asChild variant="outline" size="sm">
              <label className="cursor-pointer"><Upload className="mr-1 h-4 w-4" /> Choose files
                <input type="file" multiple hidden accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf,.doc,.docx,.txt" onChange={(e) => { void upload(e.target.files); e.target.value = ""; }} />
              </label>
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Images and documents up to 25 MB, video up to 200 MB. Originals are never modified.</p>
          {Object.entries(progress).map(([n, s]) => <div key={n} className="mt-1 flex items-center gap-2 text-xs"><Loader2 className="h-3 w-3 animate-spin" />{n}: {s}</div>)}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input aria-label="Search evidence" className="h-9 w-56 pl-8" placeholder="File, report #, keyword" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select aria-label="Status" className={sel} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select aria-label="Site" className={sel} value={site} onChange={(e) => setSite(e.target.value)}>
          <option value="">All sites</option>{c.sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {list.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No evidence yet.</div>
      ) : (
        <ul className="grid gap-2 md:grid-cols-2">
          {list.map((i: any) => {
            const Icon = i.media_kind === "image" ? ImageIcon : i.media_kind === "video" ? Film : FileText;
            return (
              <li key={i.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                <Icon className="h-5 w-5 shrink-0 text-primary" />
                <button className="min-w-0 flex-1 text-left" onClick={() => void open(i)}>
                  <div className="truncate text-sm font-medium hover:underline">{i.original_filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.incById[i.incident_id]?.reference_number ?? "D.A.R."} · {c.siteById[i.site_id]?.name ?? "No site"} · {new Date(i.created_at).toLocaleString()}
                  </div>
                  <div className={`text-xs ${i.status === "failed" ? "text-destructive" : "text-muted-foreground"}`}>{STATUS_LABEL[i.status]}{i.status_error ? ` — ${i.status_error}` : ""}</div>
                </button>
                {(i.status === "failed" || i.status === "pending_upload") && <Button size="icon" variant="ghost" aria-label="Retry" onClick={() => void retry(i.id)}><RotateCcw className="h-4 w-4" /></Button>}
              </li>
            );
          })}
        </ul>
      )}

      {preview && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">{preview.item.original_filename}</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => void download(preview.item)}>Export original</Button>
              <Button size="sm" variant="outline" onClick={() => void remove(preview.item)}>Delete</Button>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Close</Button>
            </div>
          </div>
          <div className="mt-3">
            {preview.item.media_kind === "image" && <img src={preview.url} alt={preview.item.description ?? preview.item.original_filename} className="max-h-[60vh] rounded-md" />}
            {preview.item.media_kind === "video" && <video src={preview.url} controls className="max-h-[60vh] w-full rounded-md" />}
            {preview.item.media_kind === "document" && <a href={preview.url} target="_blank" rel="noreferrer" className="text-sm underline">Open document</a>}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">No AI detections — analysis provider not configured. Add a sighting from the Sightings tab to tag this evidence manually.</p>
        </div>
      )}
    </div>
  );
}

function SightingsPanel(c: Ctx) {
  const create = useServerFn(createSighting);
  const [f, setF] = useState<any>({ category: "vehicle", timePrecision: "exact" });
  const [q, setQ] = useState(""); const [cat, setCat] = useState(""); const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: any) => setF((p: any) => ({ ...p, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await create({ data: { ...f, eventAt: f.eventAt ? new Date(f.eventAt).toISOString() : undefined } });
      toast.success(r.suggestions ? `Saved — ${r.suggestions} possible match(es) for review` : "Sighting saved");
      setF({ category: f.category, timePrecision: "exact" }); void c.reload();
    } catch (err: any) { toast.error(err?.message ?? "Could not save"); } finally { setBusy(false); }
  };

  const qn = q.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const list = c.sightings.filter((s: any) =>
    (!cat || s.category === cat) && (!from || s.event_at >= from) && (!to || s.event_at <= `${to}T23:59:59`) &&
    (!q || (qn && s.plate_normalized?.includes(qn)) ||
      `${s.description ?? ""} ${s.vehicle_make ?? ""} ${s.vehicle_model ?? ""} ${s.vehicle_color ?? ""} ${c.incById[s.incident_id]?.reference_number ?? ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      {c.canReview && (
        <form onSubmit={submit} className="space-y-2 rounded-xl border border-border bg-card p-4">
          <h2 className="font-semibold">Log a sighting</h2>
          <select aria-label="Source evidence" className={`${sel} w-full`} value={f.evidenceId ?? ""} onChange={set("evidenceId")}>
            <option value="">Source evidence (optional)</option>
            {c.items.map((i: any) => <option key={i.id} value={i.id}>{i.original_filename}</option>)}
          </select>
          <select aria-label="Incident" className={`${sel} w-full`} value={f.incidentId ?? ""} onChange={set("incidentId")}>
            <option value="">Incident (if no evidence)</option>
            {c.incidents.map((i: any) => <option key={i.id} value={i.id}>{i.reference_number}</option>)}
          </select>
          <div className="flex gap-2">
            <select aria-label="Category" className={`${sel} flex-1`} value={f.category} onChange={set("category")}>
              <option value="vehicle">Vehicle</option><option value="person">Person</option><option value="object">Object</option><option value="other">Other</option>
            </select>
            <select aria-label="Time precision" className={`${sel} flex-1`} value={f.timePrecision} onChange={set("timePrecision")}>
              <option value="exact">Exact time</option><option value="approximate">Approximate</option><option value="date_only">Date only</option><option value="unknown">Unknown</option>
            </select>
          </div>
          <Input aria-label="When" type="datetime-local" value={f.eventAt ?? ""} onChange={set("eventAt")} />
          <Input aria-label="Description" placeholder="Description (clothing, object, behaviour — no identity guesses)" value={f.description ?? ""} onChange={set("description")} />
          {f.category === "vehicle" && (
            <>
              <div className="flex gap-2">
                <Input aria-label="Color" placeholder="Color" value={f.color ?? ""} onChange={set("color")} />
                <Input aria-label="Make" placeholder="Make" value={f.make ?? ""} onChange={set("make")} />
                <Input aria-label="Model" placeholder="Model" value={f.model ?? ""} onChange={set("model")} />
              </div>
              <div className="flex gap-2">
                <Input aria-label="Plate" placeholder="Plate text" value={f.plate ?? ""} onChange={set("plate")} />
                <Input aria-label="Jurisdiction" placeholder="State / region" value={f.jurisdiction ?? ""} onChange={set("jurisdiction")} />
              </div>
            </>
          )}
          {f.category !== "person" && <Input aria-label="Identifying details" placeholder="Other identifying details (dents, stickers…)" value={f.details ?? ""} onChange={set("details")} />}
          <Button type="submit" disabled={busy} className="w-full">{busy ? "Saving…" : "Save sighting"}</Button>
        </form>
      )}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Search sightings" className="h-9 w-56" placeholder="Plate, keyword, report #" value={q} onChange={(e) => setQ(e.target.value)} />
          <select aria-label="Category filter" className={sel} value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">All categories</option><option value="vehicle">Vehicle</option><option value="person">Person</option><option value="object">Object</option><option value="other">Other</option>
          </select>
          <Input aria-label="From date" type="date" className="h-9 w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input aria-label="To date" type="date" className="h-9 w-40" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {list.length === 0 ? <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No sightings.</div> : (
          <ol className="relative space-y-2 border-l border-border pl-4">
            {list.map((s: any) => <li key={s.id}><SightingCard s={s} c={c} /></li>)}
          </ol>
        )}
      </div>
    </div>
  );
}

function SightingCard({ s, c }: { s: any; c: Ctx }) {
  const inc = c.incById[s.incident_id];
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-primary/15 px-1.5 py-0.5 text-xs capitalize text-primary">{s.category}</span>
        {s.plate_text && <span className="font-mono">{s.plate_text}{s.plate_jurisdiction ? ` (${s.plate_jurisdiction})` : ""}</span>}
        <span className="text-xs text-muted-foreground">{new Date(s.event_at).toLocaleString()} · {s.time_precision} · {c.siteById[s.site_id]?.name ?? "No site"}</span>
      </div>
      {(s.vehicle_color || s.vehicle_make) && <div>{[s.vehicle_color, s.vehicle_make, s.vehicle_model].filter(Boolean).join(" ")}</div>}
      {s.description && <div className="text-muted-foreground">{s.description}</div>}
      <div className="mt-1 text-xs text-muted-foreground">
        Source: {s.source === "manual" ? "manual entry" : `${s.provider} ${s.provider_version ?? ""}`} · Plate {s.plate_verification} · Review {s.review_status}
        {inc && <> · <Link to="/incidents/$incidentId" params={{ incidentId: inc.id }} className="underline">{inc.reference_number}</Link></>}
      </div>
    </div>
  );
}

const TIER_LABEL: Record<string, string> = { exact: "Exact metadata match", probable: "Probable match", suggestion: "Unverified suggestion" };

function MatchQueue(c: Ctx) {
  const decide = useServerFn(decideMatch);
  const [filter, setFilter] = useState("pending"); const [tier, setTier] = useState("");
  const byId = Object.fromEntries(c.sightings.map((s: any) => [s.id, s]));
  const list = c.matches.filter((m: any) => (!filter || m.status === filter) && (!tier || m.tier === tier));
  const act = async (id: string, decision: string) => {
    try { await decide({ data: { matchId: id, decision } }); toast.success(`Marked ${decision}`); void c.reload(); }
    catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select aria-label="Review status" className={sel} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="pending">Pending</option><option value="confirmed">Confirmed</option><option value="rejected">Rejected</option><option value="dismissed">Dismissed</option><option value="">All</option>
        </select>
        <select aria-label="Confidence" className={sel} value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="">All confidence levels</option>{Object.entries(TIER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <p className="text-xs text-muted-foreground">A description or appearance alone is never proof of identity. Confirming links records for review; nothing is merged automatically.</p>
      {list.length === 0 ? <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No matches to review.</div> :
        list.map((m: any) => (
          <div key={m.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={`text-sm font-semibold ${m.tier === "exact" ? "text-primary" : ""}`}>{TIER_LABEL[m.tier]}</span>
              <span className="text-xs capitalize text-muted-foreground">{m.status}</span>
            </div>
            <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">{m.reasons.map((r: string) => <li key={r}>{r}</li>)}</ul>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {byId[m.sighting_a] && <SightingCard s={byId[m.sighting_a]} c={c} />}
              {byId[m.sighting_b] && <SightingCard s={byId[m.sighting_b]} c={c} />}
            </div>
            {c.canReview && m.status === "pending" && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => void act(m.id, "confirmed")}>Confirm</Button>
                <Button size="sm" variant="outline" onClick={() => void act(m.id, "rejected")}>Reject</Button>
                <Button size="sm" variant="ghost" onClick={() => void act(m.id, "dismissed")}>Dismiss</Button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function CasesPanel(c: Ctx) {
  const mk = useServerFn(createCase);
  const link = useServerFn(linkToCase);
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [lt, setLt] = useState("incident"); const [tid, setTid] = useState("");
  const make = async (e: React.FormEvent) => {
    e.preventDefault();
    try { const r = await mk({ data: { orgId: c.orgId, title } }); setTitle(""); setOpen(r.id); void c.reload(); }
    catch (err: any) { toast.error(err?.message ?? "Failed"); }
  };
  const doLink = async (caseId: string, linkType: string, targetId: string, unlink = false) => {
    try { await link({ data: { caseId, linkType, targetId, unlink } }); setTid(""); void c.reload(); }
    catch (err: any) { toast.error(err?.message ?? "Failed"); }
  };
  const options: Record<string, any[]> = {
    incident: c.incidents.map((i: any) => ({ id: i.id, label: i.reference_number })),
    sighting: c.sightings.map((s: any) => ({ id: s.id, label: `${s.category} ${s.plate_text ?? s.description ?? ""}`.slice(0, 60) })),
    evidence: c.items.map((i: any) => ({ id: i.id, label: i.original_filename })),
  };
  const label = (l: any) => options[l.link_type]?.find((o) => o.id === l.target_id)?.label ?? l.target_id.slice(0, 8);
  return (
    <div className="space-y-3">
      {c.canReview && (
        <form onSubmit={make} className="flex gap-2">
          <Input aria-label="Case title" placeholder="New case title" value={title} onChange={(e) => setTitle(e.target.value)} className="max-w-sm" />
          <Button type="submit" disabled={!title.trim()}>Create case</Button>
        </form>
      )}
      {c.cases.length === 0 ? <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No cases yet.</div> :
        c.cases.map((k: any) => {
          const ls = c.links.filter((l: any) => l.case_id === k.id);
          return (
            <div key={k.id} className="rounded-xl border border-border bg-card p-4">
              <button className="w-full text-left" onClick={() => setOpen(open === k.id ? null : k.id)}>
                <div className="font-semibold">{k.title}</div>
                <div className="text-xs text-muted-foreground">{ls.length} linked record(s) · {k.status} · {new Date(k.created_at).toLocaleDateString()}</div>
              </button>
              {open === k.id && (
                <div className="mt-3 space-y-2">
                  <ul className="space-y-1 text-sm">
                    {ls.map((l: any) => (
                      <li key={l.id} className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1">
                        <span><span className="text-xs capitalize text-muted-foreground">{l.link_type}</span> {l.link_type === "incident"
                          ? <Link to="/incidents/$incidentId" params={{ incidentId: l.target_id }} className="underline">{label(l)}</Link> : label(l)}</span>
                        {c.canReview && <Button size="sm" variant="ghost" onClick={() => void doLink(k.id, l.link_type, l.target_id, true)}>Unlink</Button>}
                      </li>
                    ))}
                  </ul>
                  {c.canReview && (
                    <div className="flex flex-wrap gap-2">
                      <select aria-label="Link type" className={sel} value={lt} onChange={(e) => { setLt(e.target.value); setTid(""); }}>
                        <option value="incident">Incident</option><option value="sighting">Sighting</option><option value="evidence">Evidence</option>
                      </select>
                      <select aria-label="Record" className={sel} value={tid} onChange={(e) => setTid(e.target.value)}>
                        <option value="">Choose…</option>{options[lt].map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      <Button size="sm" disabled={!tid} onClick={() => void doLink(k.id, lt, tid)}>Link</Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Original reports are never copied or changed; links are recorded in the audit log.</p>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
